import * as React from "react";
import { Upload, X } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  RadioGroup,
  RadioGroupItem,
  Checkbox,
  cn,
} from "@eduviaos/ui-kit";
import type { FileRef } from "@eduviaos/shared/types";
import type { ActiveFormFieldDTO } from "@/hooks/useAdmissions";

/**
 * Generic schema-driven form renderer for B2.6b admission stage forms.
 *
 * Designed as a controlled component — the parent owns the values map
 * and the field-level error map (returned from the BE on a 422
 * `PAYLOAD_INVALID` response). This shape lets the same renderer be
 * reused later for any other `purpose='admission_stage'`-style form
 * surface without re-wiring state plumbing.
 *
 * Field-type coverage:
 *   text · email · phone · number · textarea · date · select · radio
 *   · checkbox (single checkbox treated as boolean)
 *   · file_upload (Phase H0 hotfix — was previously rendering as
 *     "unsupported field type" because the case was deliberately
 *     omitted while DocumentVault was on the roadmap. Synthesized
 *     ``local-<uuid>`` file_id pointers go to the BE, which already
 *     validates mime + size; the apply-portal submit endpoint
 *     persists the refs end-to-end now that
 *     `PublicApplyFormSubmitRequest.file_refs` is wired).
 *   · section_header / help_text (display-only)
 *
 * Out of scope this phase:
 *   - multi_select (depends on a Combobox primitive in ui-kit)
 *   - signature, address composite, currency masks
 *
 * Unknown field types render a small inline diagnostic so the IA can
 * report the gap to the SA, rather than failing silently.
 */

export interface StageFormRendererProps {
  fields: ActiveFormFieldDTO[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /**
   * file_upload field state. Optional — pages that don't expect
   * file_upload fields can omit; the renderer falls back to an
   * internal in-memory list. Pass these from the parent when you
   * need to forward the refs to a submit endpoint.
   */
  fileRefs?: FileRef[];
  onFilesChange?: (next: FileRef[]) => void;
  /** field_key → human-readable error message, set after a 422. */
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
}

export function StageFormRenderer({
  fields,
  values,
  onChange,
  fileRefs,
  onFilesChange,
  fieldErrors = {},
  disabled = false,
}: StageFormRendererProps) {
  const setField = React.useCallback(
    (key: string, value: unknown) => {
      onChange({ ...values, [key]: value });
    },
    [values, onChange],
  );

  // file_upload state can either be parent-controlled (preferred,
  // when the parent will forward refs to a submit endpoint) or
  // managed internally (so the render still works on read-only or
  // payload-only surfaces without forcing every caller to plumb
  // file state — the alternative was 6 separate caller updates for
  // the H0 hotfix and a regression-prone render gap).
  const [internalFiles, setInternalFiles] = React.useState<FileRef[]>([]);
  const isFilesControlled = fileRefs !== undefined;
  const files = isFilesControlled ? fileRefs : internalFiles;
  const setFiles = React.useCallback(
    (next: FileRef[]) => {
      if (isFilesControlled) {
        onFilesChange?.(next);
      } else {
        setInternalFiles(next);
      }
    },
    [isFilesControlled, onFilesChange],
  );

  return (
    <div className="space-y-5">
      {[...fields]
        .sort((a, b) => a.order - b.order)
        .map((f) => {
          const err = fieldErrors[f.key];
          return (
            <div key={f.key} className="space-y-2">
              <FieldBody
                field={f}
                value={values[f.key]}
                onChange={(v) => setField(f.key, v)}
                files={files}
                onFilesChange={setFiles}
                disabled={disabled}
                hasError={!!err}
              />
              {f.description && !err && (
                <p className="text-muted-foreground text-xs leading-snug">{f.description}</p>
              )}
              {err && (
                <p
                  className="text-destructive text-xs font-medium leading-snug"
                  role="alert"
                  data-testid={`field-error-${f.key}`}
                >
                  {err}
                </p>
              )}
            </div>
          );
        })}
    </div>
  );
}

interface FieldBodyProps {
  field: ActiveFormFieldDTO;
  value: unknown;
  onChange: (v: unknown) => void;
  files: FileRef[];
  onFilesChange: (next: FileRef[]) => void;
  disabled: boolean;
  hasError: boolean;
}

function FieldBody({
  field,
  value,
  onChange,
  files,
  onFilesChange,
  disabled,
  hasError,
}: FieldBodyProps) {
  // Display-only rows.
  if (field.field_type === "section_header") {
    return (
      <div className="border-border/60 mt-2 border-t pt-4">
        <p className="text-foreground text-sm font-semibold">{field.label}</p>
      </div>
    );
  }
  if (field.field_type === "help_text") {
    return (
      <div className="bg-muted/40 text-muted-foreground rounded-md p-3 text-xs leading-relaxed">
        {field.label}
      </div>
    );
  }

  const required = !!(field.validation as { required?: boolean }).required;
  const labelEl = (
    <Label
      htmlFor={`field-${field.key}`}
      className={cn("text-sm font-medium", hasError ? "text-destructive" : "text-foreground")}
    >
      {field.label}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );

  const baseInputCls = cn(
    "w-full",
    hasError && "border-destructive focus-visible:ring-destructive/30",
  );

  switch (field.field_type) {
    case "text":
    case "email":
    case "phone": {
      const htmlType =
        field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : "text";
      return (
        <>
          {labelEl}
          <Input
            id={`field-${field.key}`}
            type={htmlType}
            value={(value as string | undefined) ?? ""}
            placeholder={field.placeholder ?? undefined}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputCls}
            aria-invalid={hasError || undefined}
          />
        </>
      );
    }
    case "number":
    case "currency": {
      return (
        <>
          {labelEl}
          <Input
            id={`field-${field.key}`}
            type="number"
            value={(value as string | number | undefined) ?? ""}
            placeholder={field.placeholder ?? undefined}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange("");
                return;
              }
              const parsed = Number(raw);
              onChange(Number.isFinite(parsed) ? parsed : raw);
            }}
            disabled={disabled}
            className={baseInputCls}
            aria-invalid={hasError || undefined}
          />
        </>
      );
    }
    case "date":
    case "datetime": {
      return (
        <>
          {labelEl}
          <Input
            id={`field-${field.key}`}
            type={field.field_type === "datetime" ? "datetime-local" : "date"}
            value={(value as string | undefined) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={baseInputCls}
            aria-invalid={hasError || undefined}
          />
        </>
      );
    }
    case "textarea": {
      return (
        <>
          {labelEl}
          <Textarea
            id={`field-${field.key}`}
            value={(value as string | undefined) ?? ""}
            placeholder={field.placeholder ?? undefined}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            rows={4}
            className={baseInputCls}
            aria-invalid={hasError || undefined}
          />
        </>
      );
    }
    case "select": {
      const opts = field.options ?? [];
      return (
        <>
          {labelEl}
          <Select
            value={(value as string | undefined) ?? ""}
            onValueChange={(v) => onChange(v)}
            disabled={disabled}
          >
            <SelectTrigger id={`field-${field.key}`} className={baseInputCls}>
              <SelectValue placeholder={field.placeholder ?? "Select an option"} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      );
    }
    case "radio": {
      const opts = field.options ?? [];
      return (
        <>
          {labelEl}
          <RadioGroup
            value={(value as string | undefined) ?? ""}
            onValueChange={(v) => onChange(v)}
            disabled={disabled}
            className="flex flex-col gap-2"
          >
            {opts.map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem id={`field-${field.key}-${o.value}`} value={o.value} />
                <Label
                  htmlFor={`field-${field.key}-${o.value}`}
                  className="text-foreground text-sm font-normal"
                >
                  {o.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </>
      );
    }
    case "checkbox": {
      // Single checkbox → boolean. (multi_select with checkboxes is
      // out of scope for B2.6b — see module header.)
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`field-${field.key}`}
            checked={!!value}
            onCheckedChange={(v) => onChange(v === true)}
            disabled={disabled}
            aria-invalid={hasError || undefined}
          />
          <Label
            htmlFor={`field-${field.key}`}
            className="text-foreground cursor-pointer text-sm font-normal"
          >
            {field.label}
            {required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
        </div>
      );
    }
    case "file_upload": {
      // Mirrors `FormRenderer.tsx`'s file_upload behaviour from the
      // ui-kit: synthesize a ``local-<uuid>`` file_id pointer per
      // selected blob (real DocumentVault streaming arrives in a
      // later phase). Mime + size validation lives server-side, so
      // we keep the picker permissive but display the constraints.
      const v = (field.validation ?? {}) as {
        mime_types?: string[] | null;
        max_file_size_mb?: number | null;
        multiple_files?: boolean;
      };
      const myFiles = files.filter((f) => f.field_key === field.key);
      const accept = (v.mime_types ?? []).join(",");
      const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = Array.from(e.target.files ?? []);
        if (chosen.length === 0) return;
        const newRefs: FileRef[] = chosen.map((f) => ({
          field_key: field.key,
          file_id: `local-${crypto.randomUUID()}`,
          filename: f.name,
          mime_type: f.type || "application/octet-stream",
          size_bytes: f.size,
        }));
        const next = v.multiple_files
          ? [...files, ...newRefs]
          : [...files.filter((f) => f.field_key !== field.key), ...newRefs];
        onFilesChange(next);
        e.target.value = "";
      };
      const remove = (fileId: string) => onFilesChange(files.filter((f) => f.file_id !== fileId));
      return (
        <>
          {labelEl}
          <label
            htmlFor={`field-${field.key}`}
            className={cn(
              "border-input bg-muted/30 flex cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2 text-sm",
              hasError && "border-destructive",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <span className="text-muted-foreground flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {field.placeholder ?? "Click to upload"}
            </span>
            <span className="text-muted-foreground text-xs">
              {v.mime_types?.length ? v.mime_types.join(", ") : "any"}
              {v.max_file_size_mb ? ` · ≤${v.max_file_size_mb} MB` : ""}
            </span>
            <input
              id={`field-${field.key}`}
              type="file"
              accept={accept || undefined}
              multiple={!!v.multiple_files}
              onChange={onSelect}
              className="sr-only"
              disabled={disabled}
              aria-invalid={hasError || undefined}
            />
          </label>
          {myFiles.length > 0 && (
            <ul className="mt-2 grid gap-1.5">
              {myFiles.map((f) => (
                <li
                  key={f.file_id}
                  className="border-input bg-background flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
                >
                  <span className="text-foreground truncate" title={f.filename}>
                    {f.filename}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(f.file_id)}
                    disabled={disabled}
                    aria-label={`Remove ${f.filename}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      );
    }
    default: {
      return (
        <div
          className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid={`field-unsupported-${field.key}`}
        >
          <strong>{field.label}</strong> — unsupported field type{" "}
          <code className="font-mono">{field.field_type}</code>. Ask your admin to adjust this
          stage's form, or contact support.
        </div>
      );
    }
  }
}
